(() => {
  'use strict';

  const GLOBAL_KEY = 'DeebCuratedProductsController';
  const ROOT_SELECTOR = '[data-deeb-curated-products]';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].init(document);
    return;
  }

  const controllers = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const pad = (value) => String(value).padStart(2, '0');

  const getPristineSourceMarkup = (markup) => {
    const template = document.createElement('template');
    template.innerHTML = markup;

    template.content.querySelectorAll('[data-deeb-home-media-reveal]').forEach((media) => {
      media.removeAttribute('data-deeb-home-media-reveal-ready');
      media.classList.remove('is-pending', 'is-loaded', 'is-instant', 'is-failed');
      media.setAttribute('data-deeb-home-media-reveal-pending', '');
    });

    return template.innerHTML;
  };

  class CuratedProducts {
    constructor(root) {
      this.root = root;
      this.rail = root.querySelector('[data-deeb-curated-rail]');
      this.tabs = [...root.querySelectorAll('[data-deeb-curated-tab]')];
      this.tabsHost = root.querySelector('.deeb-curated-products__tabs');
      this.templates = [...root.querySelectorAll('[data-deeb-curated-template]')];
      this.prev = root.querySelector('[data-deeb-curated-prev]');
      this.next = root.querySelector('[data-deeb-curated-next]');
      this.dotsHost = root.querySelector('[data-deeb-curated-dots]');
      this.currentEl = root.querySelector('[data-deeb-curated-current]');
      this.totalEl = root.querySelector('[data-deeb-curated-total]');
      this.panel = root.querySelector('[role="tabpanel"]');

      this.sources = new Map();
      this.activeKey = root.dataset.initialSourceKey || this.tabs[0]?.dataset.deebCuratedSourceKey || '';
      this.activePage = 0;
      this.raf = 0;
      this.scrollSettleTimer = 0;
      this.resizeTimer = 0;

      if (!(this.rail instanceof HTMLElement) || !this.tabs.length) return;

      if (this.activeKey) this.sources.set(this.activeKey, getPristineSourceMarkup(this.rail.innerHTML));

      this.templates.forEach((template) => {
        const key = template.dataset.deebCuratedSourceKey;
        if (key) this.sources.set(key, getPristineSourceMarkup(template.innerHTML));
      });

      this.onRailScroll = () => this.requestPaginationSync();
      this.onResize = () => this.handleResize();

      this.prev?.addEventListener('click', () => this.goToPage(this.activePage - 1));
      this.next?.addEventListener('click', () => this.goToPage(this.activePage + 1));
      this.rail.addEventListener('scroll', this.onRailScroll, { passive: true });

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          this.activateSource(tab.dataset.deebCuratedSourceKey || '');
        });

        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

          event.preventDefault();

          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = this.tabs.length - 1;

          this.tabs[nextIndex]?.focus();
          this.activateSource(this.tabs[nextIndex]?.dataset.deebCuratedSourceKey || '');
        });
      });

      window.addEventListener('resize', this.onResize, { passive: true });

      this.refreshDynamicRuntimes();
      this.refreshPagination();
      this.syncTabs();

      requestAnimationFrame(() => {
        this.revealActiveTabContext('auto');
      });
    }

    getSlides() {
      return [...this.rail.querySelectorAll('[data-deeb-curated-slide]')];
    }

    getPerView() {
      const raw = getComputedStyle(this.root).getPropertyValue('--deeb-curated-per-view');
      const parsed = Number.parseInt(raw, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    getPageStarts() {
      const slides = this.getSlides();
      const perView = this.getPerView();

      if (!slides.length) return [0];
      if (slides.length <= perView) return [0];

      const maxStart = Math.max(0, slides.length - perView);
      const starts = [];

      for (let start = 0; start < slides.length; start += perView) {
        starts.push(Math.min(start, maxStart));
      }

      return [...new Set(starts)];
    }

    getTargetLeft(startIndex) {
      const slides = this.getSlides();
      const first = slides[0];
      const target = slides[startIndex];

      if (!(first instanceof HTMLElement) || !(target instanceof HTMLElement)) return 0;
      return Math.max(0, target.offsetLeft - first.offsetLeft);
    }

    getNearestPage() {
      const starts = this.getPageStarts();
      if (starts.length <= 1) return 0;

      let nearestPage = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      starts.forEach((startIndex, pageIndex) => {
        const distance = Math.abs(this.rail.scrollLeft - this.getTargetLeft(startIndex));
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestPage = pageIndex;
        }
      });

      return nearestPage;
    }

    goToPage(pageIndex, behavior = reducedMotion.matches ? 'auto' : 'smooth') {
      const starts = this.getPageStarts();
      const safePage = Math.min(Math.max(pageIndex, 0), starts.length - 1);

      this.activePage = safePage;
      this.updatePaginationUi(starts.length);

      this.rail.scrollTo({
        left: this.getTargetLeft(starts[safePage]),
        behavior,
      });
    }

    requestPaginationSync() {
      if (this.raf) return;

      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        this.activePage = this.getNearestPage();
        this.updatePaginationUi(this.getPageStarts().length);
      });

      window.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = window.setTimeout(() => {
        this.activePage = this.getNearestPage();
        this.updatePaginationUi(this.getPageStarts().length);
      }, 140);
    }

    buildDots(pageCount) {
      if (!(this.dotsHost instanceof HTMLElement)) return;

      this.dotsHost.replaceChildren();

      for (let index = 0; index < pageCount; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'deeb-curated-products__dot';
        dot.setAttribute('aria-label', `Go to product page ${index + 1}`);
        dot.addEventListener('click', () => this.goToPage(index));
        this.dotsHost.append(dot);
      }
    }

    updatePaginationUi(pageCount) {
      const safeCount = Math.max(1, pageCount);
      this.activePage = Math.min(this.activePage, safeCount - 1);

      if (this.prev) this.prev.disabled = this.activePage <= 0;
      if (this.next) this.next.disabled = this.activePage >= safeCount - 1;

      if (this.dotsHost && this.dotsHost.children.length !== safeCount) {
        this.buildDots(safeCount);
      }

      [...(this.dotsHost?.children || [])].forEach((dot, index) => {
        const active = index === this.activePage;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (this.currentEl) this.currentEl.textContent = pad(this.activePage + 1);
      if (this.totalEl) this.totalEl.textContent = pad(safeCount);
    }

    refreshPagination() {
      this.activePage = this.getNearestPage();
      this.updatePaginationUi(this.getPageStarts().length);
    }

    syncTabs() {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.deebCuratedSourceKey === this.activeKey;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;

        if (active && this.panel) {
          this.panel.setAttribute('aria-labelledby', tab.id);
        }
      });
    }

    revealActiveTabContext(behavior = reducedMotion.matches ? 'auto' : 'smooth') {
      if (!(this.tabsHost instanceof HTMLElement)) return;
      if (this.tabsHost.scrollWidth <= this.tabsHost.clientWidth + 1) return;

      const activeTab = this.tabs.find(
        (tab) => tab.dataset.deebCuratedSourceKey === this.activeKey,
      );
      if (!(activeTab instanceof HTMLElement)) return;

      const hostRect = this.tabsHost.getBoundingClientRect();
      const activeRect = activeTab.getBoundingClientRect();
      const activeCenterInContent =
        this.tabsHost.scrollLeft +
        (activeRect.left - hostRect.left) +
        activeRect.width / 2;
      const rawTargetLeft =
        activeCenterInContent - this.tabsHost.clientWidth / 2;
      const maxScrollLeft = Math.max(
        0,
        this.tabsHost.scrollWidth - this.tabsHost.clientWidth,
      );
      const targetLeft = Math.min(
        Math.max(rawTargetLeft, 0),
        maxScrollLeft,
      );

      if (Math.abs(targetLeft - this.tabsHost.scrollLeft) < 1) return;

      this.tabsHost.scrollTo({
        left: targetLeft,
        behavior,
      });
    }

    refreshDynamicRuntimes() {
      document.dispatchEvent(
        new CustomEvent('deeb:wishlist:refresh', {
          detail: { scope: this.rail },
        }),
      );

      this.rail.dispatchEvent(new CustomEvent('deeb:media-reveal:content-added', { bubbles: true }));
    }

    activateSource(key) {
      if (!key || !this.sources.has(key)) return;

      if (key === this.activeKey) {
        this.revealActiveTabContext();
        return;
      }

      this.activeKey = key;
      this.rail.innerHTML = this.sources.get(key);
      this.rail.scrollLeft = 0;
      this.activePage = 0;

      this.syncTabs();
      this.refreshDynamicRuntimes();

      requestAnimationFrame(() => {
        this.refreshPagination();
        this.revealActiveTabContext();
      });
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        this.activePage = this.getNearestPage();
        this.goToPage(this.activePage, 'auto');
        this.revealActiveTabContext('auto');
      }, 120);
    }

    destroy() {
      if (this.raf) cancelAnimationFrame(this.raf);
      window.clearTimeout(this.scrollSettleTimer);
      window.clearTimeout(this.resizeTimer);
      this.rail?.removeEventListener('scroll', this.onRailScroll);
      window.removeEventListener('resize', this.onResize);
    }
  }

  const initRoot = (root) => {
    if (!(root instanceof HTMLElement) || controllers.has(root)) return;
    controllers.set(root, new CuratedProducts(root));
  };

  const init = (scope = document) => {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  const destroyWithin = (scope) => {
    controllers.forEach((controller, root) => {
      if (scope === root || (scope instanceof Element && scope.contains(root))) {
        controller.destroy();
        controllers.delete(root);
      }
    });
  };

  window[GLOBAL_KEY] = { init };

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));

  init(document);
})();
